import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogOut, User, LayoutDashboard, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { supabase } from "@/integrations/supabase/client";

type SessionUser = { email: string | null } | null;

function useSessionUser(): { user: SessionUser; ready: boolean } {
  const [user, setUser] = useState<SessionUser>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUser(session?.user ? { email: session.user.email ?? null } : null);
      setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ? { email: data.session.user.email ?? null } : null);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return { user, ready };
}

/** Desktop account menu — Sign in button when signed out, dropdown when signed in. */
export function AccountMenu() {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const { user, ready } = useSessionUser();

  if (!ready || !user) {
    return (
      <Button asChild variant="brand" size="sm" className="ml-2 hidden md:inline-flex">
        <Link to="/login">{t("common.signIn")}</Link>
      </Button>
    );
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const emailShort = (user.email ?? "").split("@")[0] || t("nav.account.label", "Account");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="ml-2 hidden md:inline-flex max-w-[180px]"
          aria-label={t("nav.account.label", "Account")}
        >
          <User className="mr-1.5 h-4 w-4" />
          <span className="truncate">{emailShort}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {user.email && (
          <>
            <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
              {user.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="cursor-pointer">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            {t("nav.account.dashboard", "Dashboard")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/profile" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            {t("nav.account.settings", "Account settings")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          {t("nav.account.signOut", "Sign out")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Mobile auth block for the sheet — stacked links + sign out, or single sign-in button. */
export function AccountMenuMobile({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const { user, ready } = useSessionUser();

  if (!ready || !user) {
    return (
      <Button asChild variant="brand" size="lg" className="w-full">
        <Link to="/login" onClick={onNavigate}>{t("common.signIn")}</Link>
      </Button>
    );
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onNavigate?.();
    navigate("/");
  };

  return (
    <div className="flex flex-col gap-2">
      {user.email && (
        <p className="px-3 text-xs text-muted-foreground truncate">{user.email}</p>
      )}
      <Button asChild variant="outline" size="lg" className="w-full justify-start">
        <Link to="/dashboard" onClick={onNavigate}>
          <LayoutDashboard className="mr-2 h-4 w-4" />
          {t("nav.account.dashboard", "Dashboard")}
        </Link>
      </Button>
      <Button asChild variant="outline" size="lg" className="w-full justify-start">
        <Link to="/settings/profile" onClick={onNavigate}>
          <Settings className="mr-2 h-4 w-4" />
          {t("nav.account.settings", "Account settings")}
        </Link>
      </Button>
      <Button variant="outline" size="lg" className="w-full justify-start text-destructive" onClick={handleSignOut}>
        <LogOut className="mr-2 h-4 w-4" />
        {t("nav.account.signOut", "Sign out")}
      </Button>
    </div>
  );
}